import { Divider } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import { GetImageListReq } from '@/api/type-request'
import { ServerImageInfo, ServerSnapInfo } from '@/api/type-server.ts'
import CopyIcon from '@/assets/icons/copy.svg?react'
import PencilIcon from '@/assets/icons/pencil.svg?react'
import { Loading, message, Trans } from '@/components'
import { MachineAttrTag } from '@/consts/image'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import EditMaxReplicasModal from '@/pages/auto-deploy/components/EditMaxReplicasModal'
import EditTtlModal from '@/pages/auto-deploy/components/EditTtlModal'
import { formatRamMB } from '@/utils/formatter.ts'
import { fetchAllPages } from '@/utils/pagination'

const IMAGE_PAGE_SIZE = 99

async function findImageAcrossPages(
  imageId: number,
  filters: Omit<GetImageListReq, 'page' | 'per_page'>,
): Promise<ServerImageInfo | undefined> {
  let page = 1
  let totalPages = 1

  do {
    const res = await API.getImageList({
      ...filters,
      page,
      per_page: IMAGE_PAGE_SIZE,
    })

    if (res.code !== 0) return undefined

    const image = res.images.find((item) => item.id === imageId)
    if (image) return image

    const pageSize = res.pagination.perPage || IMAGE_PAGE_SIZE
    totalPages = Math.ceil(res.pagination.total / pageSize)
    if (res.images.length === 0) return undefined

    page += 1
  } while (page <= totalPages)

  return undefined
}

interface props {
  _deployment: Deployment
  _enabled?: boolean
  onUpdate?: () => void
}

export default function DeploymentTarget({ _deployment, _enabled, onUpdate }: props) {
  const { t } = useTranslation('autoDeploy')
  const { mode, teamResID } = useUnifiedContext()
  const [loading, setLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [imageSpec, setImageSpec] = useState<ServerImageInfo>()
  const [snapSpec, setSnapSpec] = useState<ServerSnapInfo>()
  const [state, copyToClipboard] = useCopyToClipboard()
  const [domains, setDomains] = useState<number[]>([])

  // Modal states
  const [showTtlModal, setShowTtlModal] = useState(false)
  const [showMaxReplicasModal, setShowMaxReplicasModal] = useState(false)

  // Fetch domains on mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await API.getDomainList()
        if (res.code === 0) {
          setDomains(res.domainInfos?.map((d) => d.domain) || [])
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error)
      }
    }
    fetchDomains()
  }, [])

  const getImages = useCallback(
    async (imageId: number) => {
      setLoading(true)

      try {
        const filters = {
          machine_category: _deployment.runtimeEnv.hardwareEnv.resource.machineCategory,
        }
        let image = await findImageAcrossPages(imageId, filters)

        // The unfiltered endpoint currently returns container images by default.
        // VM images must be requested with their machine attribute tag.
        if (!image) {
          image = await findImageAcrossPages(imageId, {
            ...filters,
            machine_attr_tag: MachineAttrTag.VirtualMachine,
          })
        }

        setImageSpec(image)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
        setHasFetched(true)
      }
    },
    [_deployment.resID, _deployment.runtimeEnv.hardwareEnv.resource.machineCategory],
  )

  const getSnapshots = useCallback(
    (snapshotId: number) => {
      setLoading(true)

      if (mode === 'platform') {
        // Platform mode: only query personal snapshots
        fetchAllPages({
          fetchPage: (page, pageSize) =>
            API.getSnapList({ page, per_page: pageSize, domains: [-1] }),
          getItems: (response) => response.snapshots,
        })
          .then((snapshots) => {
            setSnapSpec(snapshots.find((snapshot) => Number(snapshot.id) === snapshotId))
          })
          .catch((error) => {
            console.error(error)
          })
          .finally(() => {
            setLoading(false)
            setHasFetched(true)
          })
      } else {
        // Team mode: wait for domains to be loaded before querying
        if (domains.length === 0) {
          setLoading(false)
          return
        }

        // Team mode: query both personal and team shared snapshots in parallel
        Promise.all([
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getSnapList({ page, per_page: pageSize, domains: [-1] }),
            getItems: (response) => response.snapshots,
          }),
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getTeamSharedSnapshots({
                page,
                per_page: pageSize,
                res_id: teamResID!,
                regions: domains,
              }),
            getItems: (response) => response.snapshots,
          }),
        ])
          .then(([personalSnapshots, teamSnapshots]) => {
            // First check personal snapshots
            const personalSnapshot = personalSnapshots.find(
              (snapshot) => Number(snapshot.id) === snapshotId,
            )
            if (personalSnapshot) {
              setSnapSpec(personalSnapshot)
              return
            }

            // Then check team shared snapshots
            const teamSnapshot = teamSnapshots.find(
              (snapshot) => Number(snapshot.snapshot.id) === snapshotId,
            )
            if (teamSnapshot) {
              setSnapSpec(teamSnapshot.snapshot)
            }
          })
          .catch((error) => {
            console.error(error)
          })
          .finally(() => {
            setLoading(false)
            setHasFetched(true)
          })
      }
    },
    [_deployment.resID, mode, teamResID, domains],
  )

  const memoizedDeployment = useMemo(() => _deployment, [_deployment.resID])

  useEffect(() => {
    if (memoizedDeployment) {
      const { fromSnapshotId, fromImageId } = memoizedDeployment.runtimeEnv.systemEnv

      if (fromImageId !== 0) {
        getImages(memoizedDeployment.runtimeEnv.systemEnv.fromImageId)
      }

      if (fromSnapshotId !== 0) {
        getSnapshots(memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId)
      }
    }
  }, [
    getImages,
    getSnapshots,
    memoizedDeployment.runtimeEnv.systemEnv.fromImageId,
    memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId,
  ])

  // Handle case where no fetch is needed (both IDs are 0)
  useEffect(() => {
    const { fromSnapshotId, fromImageId } = _deployment.runtimeEnv.systemEnv
    if (fromImageId === 0 && fromSnapshotId === 0) {
      setHasFetched(true)
    }
  }, [_deployment.runtimeEnv.systemEnv])

  useEffect(() => {
    if (state.value) {
      message.success(t('common.copied'))
    }
  }, [state, t])

  return (
    <>
      {(loading || !hasFetched) && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && hasFetched && (
        <div className="bg-neutral-50 rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
          <div className="w-full grid grid-cols-2 gap-y-4 rounded-lg">
            <div className="col-span-1 grid grid-cols-2 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="common.id" ns="autoDeploy">
                    ID
                  </Trans>
                </div>
                <div className="t-sb text-primary">{_deployment.resID}</div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.name" ns="autoDeploy">
                    Auto Deploy Name
                  </Trans>
                </div>
                <div className="t-sb text-primary">{_deployment.name}</div>
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.description" ns="autoDeploy">
                    Auto Deploy Description
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.description}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.instancePreview" ns="autoDeploy">
                Instance Preview
              </Trans>
            </div>
            <Divider className="my-0 pr-4" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            {_deployment.runtimeEnv.systemEnv.fromImageId !== 0 && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.image" ns="autoDeploy">
                      Image
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {imageSpec?.alias || _deployment.runtimeEnv.systemEnv.fromImageId}
                  </div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.imageDescription" ns="autoDeploy">
                      Image description
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">{imageSpec?.description || '-'}</div>
                </div>
              </div>
            )}
            {_deployment.runtimeEnv.systemEnv.fromSnapshotId !== 0 && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.snapshot" ns="autoDeploy">
                      Snapshot
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {snapSpec?.flag?.subject || _deployment.runtimeEnv.systemEnv.fromSnapshotId}
                  </div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.snapshotCreatedTime" ns="autoDeploy">
                      Snapshot created time
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {snapSpec?.flag
                      ? dayjs(snapSpec.flag.createTime).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </div>
                </div>
              </div>
            )}
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpuModel" ns="autoDeploy">
                    GPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpuName || '-'}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpus" ns="autoDeploy">
                    GPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpus}
                </div>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpuRam" ns="autoDeploy">
                    GPU RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.gpuRamMB)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.cpuModel" ns="autoDeploy">
                    CPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpuName}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.vCpus" ns="autoDeploy">
                    vCPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpus}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.ram" ns="autoDeploy">
                    RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.ramMB)}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.storage" ns="autoDeploy">
                    Storage
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.diskGB}{' '}
                  <Trans i18nKey="common.units.gb" ns="autoDeploy">
                    GB
                  </Trans>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.cmd" ns="autoDeploy">
                Service & Start Command
              </Trans>
            </div>
            <Divider className="my-0 pr-2" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            <div className="col-span-4 grid grid-cols-3 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.accessMethodTitle" ns="autoDeploy">
                    Access Method
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  <Trans i18nKey="deployTarget.sharedMethod" ns="autoDeploy">
                    Shared Method
                  </Trans>
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">URL</div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  <div>{_deployment.gateway.url}</div>
                  <div
                    className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                    onClick={() => {
                      copyToClipboard(_deployment.gateway.url)
                    }}
                  >
                    <CopyIcon width={16} height={16} />
                    {t('common.copy')}
                  </div>
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.port" ns="autoDeploy">
                    Port
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.gateway.port > 0 ? (
                    <>
                      <div>{_deployment.gateway.port}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.gateway.port.toString())
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        {t('common.copy')}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.cmd" ns="autoDeploy">
                    Start Command
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.runtimeEnv.systemEnv.cmd.length > 0 ? (
                    <>
                      <div>{_deployment.runtimeEnv.systemEnv.cmd}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.runtimeEnv.systemEnv.cmd)
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        {t('common.copy')}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.deploymentControl" ns="autoDeploy">
                Deployment Control
              </Trans>
            </div>
            <Divider className="my-0 pr-2" />
          </div>

          <div className="w-full grid grid-cols-4 gap-x-4">
            <div className="col-span-1 space-y-1 group">
              <div className="t-xs text-secondary flex items-center gap-1">
                <Trans i18nKey="deployTarget.ttlSeconds" ns="autoDeploy">
                  Instance Idle Retention Period
                </Trans>
              </div>
              <div className="flex items-center gap-1">
                <div className="t-sb text-primary">
                  {(_deployment.ttlSeconds || 0) / 60}{' '}
                  <span className="text-tertiary">
                    <Trans i18nKey="common.units.minutes" ns="autoDeploy">
                      min
                    </Trans>
                  </span>
                </div>
                <PencilIcon
                  className="w-4 h-4 text-highlight cursor-pointer hidden group-hover:block"
                  onClick={() => setShowTtlModal(true)}
                />
              </div>
            </div>
            <div className="col-span-1 space-y-1 group">
              <div className="t-xs text-secondary">
                <Trans i18nKey="deployTarget.maxNumberIns" ns="autoDeploy">
                  Maximum Number of Instances
                </Trans>
              </div>
              <div className="flex items-center gap-1">
                <div className="t-sb text-primary">{_deployment.maxReplicas}</div>

                <PencilIcon
                  className="w-4 h-4 text-highlight cursor-pointer hidden group-hover:block"
                  onClick={() => setShowMaxReplicasModal(true)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit TTL Modal */}
      <EditTtlModal
        open={showTtlModal}
        onClose={() => setShowTtlModal(false)}
        deployment={_deployment}
        enabled={_enabled}
        onUpdate={onUpdate}
      />

      {/* Edit Max Replicas Modal */}
      <EditMaxReplicasModal
        open={showMaxReplicasModal}
        onClose={() => setShowMaxReplicasModal(false)}
        deployment={_deployment}
        enabled={_enabled}
        onUpdate={onUpdate}
      />
    </>
  )
}
